/**
 * Roulottes Lupien Chatbot Widget — v2.0
 * boutique.roulotteslupien.ca
 *
 * Self-initializes from <script data-rl-chat data-*="..."> attrs:
 *   data-supabase-url
 *   data-supabase-anon-key
 *   data-color-primary (default #ED6400)
 *   data-color-secondary (default #173461)
 */
(function () {
  'use strict';
  if (window.__RL_CHAT_LOADED__) return;
  window.__RL_CHAT_LOADED__ = true;

  // ===================================================================
  // CONFIG
  // ===================================================================
  const scriptTag = document.querySelector('script[data-rl-chat]') || document.currentScript;
  const CONFIG = {
    supabaseUrl: scriptTag?.dataset.supabaseUrl || '',
    supabaseAnonKey: scriptTag?.dataset.supabaseAnonKey || '',
    colorPrimary: scriptTag?.dataset.colorPrimary || '#ED6400',
    colorSecondary: scriptTag?.dataset.colorSecondary || '#173461',
  };
  if (!CONFIG.supabaseUrl || !CONFIG.supabaseAnonKey) {
    console.error('[RL Chat] Missing Supabase credentials in script tag.');
    return;
  }

  // ===================================================================
  // STATE
  // ===================================================================
  let sessionId = null;
  let conversationId = null;
  let messagesArr = [];           // {role, content} OpenAI-style for ai-chat-boutique
  let supabaseClient = null;
  let realtimeChannel = null;
  let isPanelOpen = false;
  let isHistoryOpen = false;
  let isSending = false;
  let fab, panel, messagesEl, inputEl, sendBtn, historyEl, historyListEl;

  // ===================================================================
  // I18N
  // ===================================================================
  function getLang() {
    // Source de vérité : l'attribut lang de <html>, défini par Shopify
    // (<html lang="{{ request.locale.iso_code }}">). Fiable quelle que soit
    // la façon dont Shopify sert la langue (préfixe d'URL, cookie, marché).
    var htmlLang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
    if (htmlLang.indexOf('en') === 0) return 'en';
    if (htmlLang.indexOf('fr') === 0) return 'fr';
    // Repli : heuristique basée sur le chemin d'URL. Accepte « /en » comme
    // « /en/ » (la page d'accueil anglaise est /en, sans slash final).
    var path = window.location.pathname;
    return (path === '/en' || path.indexOf('/en/') === 0) ? 'en' : 'fr';
  }
  function rlVisitorId() {
    const m = document.cookie.match(/(?:^|;\s*)_shopify_y=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }
  const T = {
    fr: {
      title: 'Roulottes Lupien', subtitle: 'Assistant IA',
      placeholder: 'Tapez votre message...', send: 'Envoyer',
      close: 'Fermer', history: 'Historique',
      closeConv: 'Fermer cette conversation',
      newConv: 'Nouvelle conversation',
      historyTitle: 'Conversations passées',
      historyEmpty: 'Aucune conversation passée.',
      historyLoading: 'Chargement...',
      historyError: 'Erreur de chargement.',
      activeBadge: 'Active', escalatedBadge: 'Avec agent',
      errorMsg: 'Une erreur est survenue. Veuillez réessayer.',
      confirmClose: 'Fermer cette conversation et en démarrer une nouvelle ?',
      voirProduit: 'Voir le produit', backToChat: 'Retour',
      noMessage: '(Pas de message)',
    },
    en: {
      title: 'Roulottes Lupien', subtitle: 'AI Assistant',
      placeholder: 'Type your message...', send: 'Send',
      close: 'Close', history: 'History',
      closeConv: 'End this conversation',
      newConv: 'New conversation',
      historyTitle: 'Past conversations',
      historyEmpty: 'No past conversations.',
      historyLoading: 'Loading...',
      historyError: 'Loading error.',
      activeBadge: 'Active', escalatedBadge: 'With agent',
      errorMsg: 'An error occurred. Please try again.',
      confirmClose: 'End this conversation and start a new one?',
      voirProduit: 'View product', backToChat: 'Back',
      noMessage: '(No message)',
    },
  };
  const currentPageUrl = window.location.pathname;   // ou window.location.href
  // NE PAS figer l'identifiant de visiteur ici.
  //
  // Le cookie _shopify_y est écrit par le script d'analytique de Shopify,
  // qui se charge de façon asynchrone. Lu au chargement de CE script, il
  // peut ne pas exister encore chez un visiteur de première visite — et la
  // conversation naissait alors sans visiteur, définitivement : rien ne
  // peut la rattacher à une navigation après coup, faute de clé de session
  // sur les événements de navigation.
  //
  // theme.js, sur la même page, lit le même cookie AU MOMENT de chaque
  // événement, et le trouve toujours. rlVisitorId() fait pareil — elle
  // était écrite juste au-dessus, et cette constante l'avait mise au
  // chômage. On l'appelle à l'envoi, pas au chargement.
  function t(k) { return T[getLang()][k] ?? T.fr[k] ?? k; }

  // ===================================================================
  // SESSION
  // ===================================================================
  function initSession() {
    sessionId = localStorage.getItem('rl_chat_session_id');
    if (!sessionId) {
      sessionId = (crypto.randomUUID && crypto.randomUUID()) ||
        (Date.now() + '-' + Math.random().toString(36).slice(2));
      localStorage.setItem('rl_chat_session_id', sessionId);
    }
    conversationId = localStorage.getItem('rl_chat_conversation_id') || null;
  }

  // ===================================================================
  // STYLES
  // ===================================================================
  function injectStyles() {
    const css = `
.rl-chat-fab {
  position: fixed; bottom: 24px; right: 24px;
  width: 60px; height: 60px; border-radius: 50%;
  background: ${CONFIG.colorPrimary}; color: #fff;
  border: none; cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,.2);
  z-index: 9998;
  display: flex; align-items: center; justify-content: center;
  transition: transform .2s;
}
.rl-chat-fab:hover { transform: scale(1.05); }
.rl-chat-fab svg { width: 28px; height: 28px; }
.rl-chat-panel {
  position: fixed; bottom: 24px; right: 24px;
  width: 380px; height: 580px; max-height: calc(100vh - 48px);
  background: #fff; border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,.18);
  z-index: 9999;
  display: none; flex-direction: column; overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
}
.rl-chat-panel.open { display: flex; }
@media (max-width: 480px) {
  .rl-chat-panel {
    width: 100vw; height: 100vh; max-height: 100vh;
    bottom: 0; right: 0; border-radius: 0;
  }
}
.rl-chat-header {
  background: ${CONFIG.colorSecondary}; color: #fff;
  padding: 14px 16px;
  display: flex; align-items: center; gap: 12px;
  flex-shrink: 0;
}
.rl-chat-avatar {
  width: 36px; height: 36px; border-radius: 50%;
  background: ${CONFIG.colorPrimary}; color: #fff;
  font-weight: 700; font-size: 16px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.rl-chat-title-block { flex-grow: 1; min-width: 0; }
.rl-chat-name { font-weight: 600; font-size: 14px; line-height: 1.2; }
.rl-chat-subtitle { font-size: 12px; opacity: .85; line-height: 1.2; margin-top: 2px; }
.rl-chat-header-actions { display: flex; gap: 4px; }
.rl-chat-icon-btn {
  background: transparent; border: none; color: #fff;
  cursor: pointer; padding: 6px; border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
}
.rl-chat-icon-btn:hover { background: rgba(255,255,255,.15); }
.rl-chat-icon-btn svg { width: 18px; height: 18px; }
.rl-chat-body {
  flex-grow: 1; overflow: hidden;
  display: flex; flex-direction: column;
  position: relative; background: #f8f9fa;
}
.rl-chat-messages {
  flex-grow: 1; overflow-y: auto;
  padding: 16px;
  display: flex; flex-direction: column; gap: 12px;
}
.rl-chat-msg-wrap { display: flex; }
.rl-chat-msg-wrap.user { justify-content: flex-end; }
.rl-chat-msg-wrap.assistant, .rl-chat-msg-wrap.system { justify-content: flex-start; }
.rl-chat-msg-wrap.system { justify-content: center; }
.rl-chat-msg {
  max-width: 80%; padding: 10px 14px;
  border-radius: 16px; font-size: 14px; line-height: 1.4;
  word-wrap: break-word; overflow-wrap: break-word;
}
.rl-chat-msg.user {
  background: ${CONFIG.colorPrimary}; color: #fff;
  border-bottom-right-radius: 4px;
}
.rl-chat-msg.assistant {
  background: #fff; color: #1a1a1a;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 4px;
}
.rl-chat-msg.assistant.agent { border: 2px solid ${CONFIG.colorPrimary}; }
.rl-chat-msg.system {
  background: transparent; color: #6b7280;
  font-size: 12px; font-style: italic; text-align: center;
  border: none; padding: 4px 12px;
}
.rl-chat-msg-meta {
  font-size: 11px; color: ${CONFIG.colorSecondary};
  font-weight: 600; margin-bottom: 4px;
}
.rl-chat-msg a { color: ${CONFIG.colorPrimary}; text-decoration: underline; }
.rl-chat-msg code {
  background: rgba(0,0,0,.06); padding: 1px 4px; border-radius: 3px;
  font-family: monospace; font-size: 13px;
}
.rl-chat-typing-dots::after {
  content: '...';
  display: inline-block;
  animation: rl-chat-dots 1.4s infinite;
}
@keyframes rl-chat-dots {
  0%, 20% { content: '.'; }
  40% { content: '..'; }
  60%, 100% { content: '...'; }
}
.rl-chat-products {
  display: flex; flex-direction: column; gap: 8px;
  max-width: 90%;
}
.rl-chat-product {
  display: flex; gap: 10px;
  background: #fff; border: 1px solid #e5e7eb;
  border-radius: 10px; padding: 10px;
  text-decoration: none; color: inherit;
  transition: border-color .15s, transform .15s;
}
.rl-chat-product:hover { border-color: ${CONFIG.colorPrimary}; transform: translateY(-1px); }
.rl-chat-product-img {
  width: 64px; height: 64px; object-fit: contain;
  background: #f8f9fa; border-radius: 6px; flex-shrink: 0;
}
.rl-chat-product-info { flex-grow: 1; min-width: 0; }
.rl-chat-product-title {
  font-size: 13px; font-weight: 600;
  color: ${CONFIG.colorSecondary};
  margin-bottom: 4px; line-height: 1.3;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.rl-chat-product-price { font-size: 14px; font-weight: 700; color: ${CONFIG.colorPrimary}; }
.rl-chat-product-cta {
  font-size: 11px; color: ${CONFIG.colorPrimary};
  text-transform: uppercase; font-weight: 600; margin-top: 4px;
}
.rl-chat-footer { flex-shrink: 0; border-top: 1px solid #e5e7eb; background: #fff; }
.rl-chat-close-conv-btn {
  width: 100%; background: transparent; border: none;
  padding: 8px 12px; font-size: 12px; color: #6b7280;
  cursor: pointer; font-family: inherit;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  border-bottom: 1px solid #f3f4f6;
}
.rl-chat-close-conv-btn:hover { background: #f9fafb; color: #1a1a1a; }
.rl-chat-close-conv-btn svg { width: 14px; height: 14px; }
.rl-chat-input-row { display: flex; gap: 8px; padding: 10px 12px; align-items: center; }
.rl-chat-input {
  flex-grow: 1; border: 1px solid #e5e7eb; border-radius: 24px;
  padding: 10px 14px; font-size: 14px; outline: none; font-family: inherit;
}
.rl-chat-input:focus { border-color: ${CONFIG.colorPrimary}; }
.rl-chat-send-btn {
  background: ${CONFIG.colorSecondary}; color: #fff; border: none;
  width: 38px; height: 38px; border-radius: 50%; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; transition: background .15s;
}
.rl-chat-send-btn:hover:not(:disabled) { background: ${CONFIG.colorPrimary}; }
.rl-chat-send-btn:disabled { opacity: .5; cursor: not-allowed; }
.rl-chat-send-btn svg { width: 16px; height: 16px; }
.rl-chat-history {
  position: absolute; inset: 0; background: #fff;
  display: none; flex-direction: column; z-index: 2;
}
.rl-chat-history.open { display: flex; }
.rl-chat-history-header {
  padding: 14px 16px; border-bottom: 1px solid #e5e7eb;
  display: flex; align-items: center; gap: 12px;
  flex-shrink: 0; background: #fff;
}
.rl-chat-history-back {
  background: transparent; border: none; cursor: pointer;
  padding: 6px; border-radius: 6px; color: ${CONFIG.colorSecondary};
  display: flex; align-items: center; justify-content: center;
}
.rl-chat-history-back:hover { background: #f3f4f6; }
.rl-chat-history-back svg { width: 20px; height: 20px; }
.rl-chat-history-title-text {
  flex-grow: 1; font-weight: 600; font-size: 15px;
  color: ${CONFIG.colorSecondary};
}
.rl-chat-new-conv-btn {
  background: ${CONFIG.colorPrimary}; color: #fff; border: none;
  padding: 6px 12px; border-radius: 16px;
  font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit;
}
.rl-chat-new-conv-btn:hover { opacity: .9; }
.rl-chat-history-list {
  flex-grow: 1; overflow-y: auto; padding: 12px;
  display: flex; flex-direction: column; gap: 8px;
}
.rl-chat-history-card {
  background: #f8f9fa; border: 1px solid #e5e7eb;
  border-radius: 10px; padding: 12px;
  cursor: pointer; transition: border-color .15s;
}
.rl-chat-history-card:hover { border-color: ${CONFIG.colorPrimary}; }
.rl-chat-history-card.active { border-color: ${CONFIG.colorPrimary}; background: #fff; }
.rl-chat-history-preview {
  font-size: 14px; color: #1a1a1a; line-height: 1.3; margin-bottom: 6px;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.rl-chat-history-meta { display: flex; align-items: center; gap: 8px; font-size: 11px; color: #6b7280; }
.rl-chat-history-badge {
  background: ${CONFIG.colorSecondary}; color: #fff;
  padding: 2px 6px; border-radius: 10px;
  font-size: 10px; font-weight: 600;
}
.rl-chat-history-badge.active { background: ${CONFIG.colorPrimary}; }
.rl-chat-history-badge.escalated { background: #f59e0b; }
.rl-chat-history-empty, .rl-chat-history-loading, .rl-chat-history-error {
  text-align: center; padding: 40px 20px; color: #6b7280; font-size: 14px;
}
/* ===== CTA Cards (handoff agent / formulaire callback) ===== */
.rl-cta-card {
  background: #f8f9fa;
  border: 1px solid #e1e4e8;
  border-left: 3px solid #ED6400;
  border-radius: 6px;
  padding: 12px;
  margin: 8px 12px;
  font-size: 13px;
  animation: rl-cta-slide-in 0.25s ease-out;
}

@keyframes rl-cta-slide-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.rl-cta-info {
  color: #4a5568;
  margin-bottom: 10px;
  line-height: 1.4;
}

.rl-cta-form-title {
  font-size: 14px;
  font-weight: 600;
  color: #173461;
  margin: 0 0 10px;
}

.rl-cta-field {
  margin-bottom: 8px;
}

.rl-cta-field label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 2px;
}

.rl-required {
  color: #ED6400;
}

.rl-cta-field input,
.rl-cta-field select,
.rl-cta-field textarea {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  font-size: 13px;
  font-family: inherit;
  background: white;
  box-sizing: border-box;
  color: #1a202c;
}

.rl-cta-field textarea {
  resize: vertical;
  min-height: 70px;
  line-height: 1.4;
}

.rl-cta-field input:focus,
.rl-cta-field select:focus,
.rl-cta-field textarea:focus {
  outline: none;
  border-color: #173461;
  box-shadow: 0 0 0 2px rgba(23, 52, 97, 0.15);
}

.rl-cta-help {
  font-size: 11px;
  color: #718096;
  margin: 4px 0 0;
  font-style: italic;
  line-height: 1.3;
}

.rl-cta-button {
  background: #173461;
  color: white;
  border: none;
  padding: 9px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  margin-top: 10px;
  font-family: inherit;
  transition: background 0.15s;
}

.rl-cta-button:hover:not(:disabled) {
  background: #0f2547;
}

.rl-cta-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.rl-cta-confirmation {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
  padding: 10px 12px;
  margin: 8px 12px;
  font-size: 13px;
  color: #065f46;
  line-height: 1.4;
}

.rl-cta-confirmation svg {
  flex-shrink: 0;
  margin-top: 2px;
  color: #047857;
}

.rl-cta-error {
  color: #c53030;
  font-size: 12px;
  margin-top: 8px;
  padding: 6px 8px;
  background: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 4px;
}`;
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);
  }

  // ===================================================================
  // DOM
  // ===================================================================
  function buildDOM() {
    fab = document.createElement('button');
    fab.className = 'rl-chat-fab';
    fab.setAttribute('aria-label', t('title'));
    fab.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/><circle cx="9" cy="10" r="1.2"/><circle cx="12" cy="10" r="1.2"/><circle cx="15" cy="10" r="1.2"/></svg>`;
    fab.onclick = togglePanel;
    document.body.appendChild(fab);

    panel = document.createElement('div');
    panel.className = 'rl-chat-panel';
    panel.innerHTML = `
      <div class="rl-chat-header">
        <div class="rl-chat-avatar">R</div>
        <div class="rl-chat-title-block">
          <div class="rl-chat-name">${esc(t('title'))}</div>
          <div class="rl-chat-subtitle">${esc(t('subtitle'))}</div>
        </div>
        <div class="rl-chat-header-actions">
          <button class="rl-chat-icon-btn rl-chat-history-btn" aria-label="${esc(t('history'))}" title="${esc(t('history'))}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l4 2"/></svg>
          </button>
          <button class="rl-chat-icon-btn rl-chat-close-btn" aria-label="${esc(t('close'))}" title="${esc(t('close'))}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>
      <div class="rl-chat-body">
        <div class="rl-chat-messages" role="log" aria-live="polite"></div>
        <div class="rl-chat-history">
          <div class="rl-chat-history-header">
            <button class="rl-chat-history-back" aria-label="${esc(t('backToChat'))}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <div class="rl-chat-history-title-text">${esc(t('historyTitle'))}</div>
            <button class="rl-chat-new-conv-btn">${esc(t('newConv'))}</button>
          </div>
          <div class="rl-chat-history-list"></div>
        </div>
      </div>
      <div class="rl-chat-footer">
        <button class="rl-chat-close-conv-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
          ${esc(t('closeConv'))}
        </button>
        <div class="rl-chat-input-row">
          <input type="text" class="rl-chat-input" placeholder="${esc(t('placeholder'))}" aria-label="${esc(t('placeholder'))}"/>
          <button class="rl-chat-send-btn" aria-label="${esc(t('send'))}">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
          </button>
        </div>
      </div>`;
    document.body.appendChild(panel);

    messagesEl = panel.querySelector('.rl-chat-messages');
    inputEl = panel.querySelector('.rl-chat-input');
    sendBtn = panel.querySelector('.rl-chat-send-btn');
    historyEl = panel.querySelector('.rl-chat-history');
    historyListEl = panel.querySelector('.rl-chat-history-list');

    panel.querySelector('.rl-chat-close-btn').onclick = togglePanel;
    panel.querySelector('.rl-chat-history-btn').onclick = openHistory;
    panel.querySelector('.rl-chat-history-back').onclick = closeHistory;
    panel.querySelector('.rl-chat-new-conv-btn').onclick = startNewConversation;
    panel.querySelector('.rl-chat-close-conv-btn').onclick = handleCloseCurrentConv;
    sendBtn.onclick = sendMessage;
    inputEl.onkeydown = (e) => { if (e.key === 'Enter') sendMessage(); };
    inputEl.addEventListener('input', broadcastTyping);
  }

  function togglePanel() {
    isPanelOpen = !isPanelOpen;
    panel.classList.toggle('open', isPanelOpen);
    fab.style.display = isPanelOpen ? 'none' : 'flex';
    if (isPanelOpen) setTimeout(() => inputEl?.focus(), 100);
  }

  // ===================================================================
  // SUPABASE SDK
  // ===================================================================
  function loadSupabaseSDK() {
    if (window.supabase?.createClient) { initSupabaseClient(); return; }
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2/dist/umd/supabase.min.js';
    s.async = true;
    s.onload = initSupabaseClient;
    s.onerror = () => console.error('[RL Chat] Failed to load Supabase SDK');
    document.head.appendChild(s);
  }
  function initSupabaseClient() {
    try {
      supabaseClient = window.supabase.createClient(CONFIG.supabaseUrl, CONFIG.supabaseAnonKey);
      if (conversationId) loadConversationMessages(conversationId);
    } catch (e) { console.error('[RL Chat] Supabase init failed:', e); }
  }

  // ===================================================================
  // RENDER
  // ===================================================================
  function addMessage(role, content, opts = {}) {
    if (!content && role !== 'system') return;
    const wrap = document.createElement('div');
    wrap.className = `rl-chat-msg-wrap ${role}`;
    const msg = document.createElement('div');
    msg.className = `rl-chat-msg ${role}`;
    if (opts.fromAgent) msg.classList.add('agent');
    if (opts.fromAgent && opts.agentName) {
      const meta = document.createElement('div');
      meta.className = 'rl-chat-msg-meta';
      meta.textContent = opts.agentName;
      msg.appendChild(meta);
    }
    const text = document.createElement('div');
    text.innerHTML = renderMarkdown(content || '');
    msg.appendChild(text);
    wrap.appendChild(msg);
    messagesEl.appendChild(wrap);
    requestAnimationFrame(() => { messagesEl.scrollTop = messagesEl.scrollHeight; });
    return wrap;
  }

  function addProductCards(products) {
    if (!products?.length) return;
    const wrap = document.createElement('div');
    wrap.className = 'rl-chat-msg-wrap assistant';
    const container = document.createElement('div');
    container.className = 'rl-chat-products';
    const lang = getLang();
    const prefix = lang === 'en' ? '/en' : '';
    products.slice(0, 6).forEach(p => {
      const a = document.createElement('a');
      a.className = 'rl-chat-product';
      a.href = `${prefix}/products/${encodeURIComponent(p.handle)}`;
      a.target = '_top';
      const img = p.image_url || p.featured_image_url || p.image || '';
      const title = (lang === 'en' ? p.title_en : p.title_fr) || p.title || p.handle || '';
      const price = p.price_min || p.price || '';
      a.innerHTML = `
        ${img ? `<img class="rl-chat-product-img" src="${esc(img)}" alt="${esc(title)}" loading="lazy"/>` : ''}
        <div class="rl-chat-product-info">
          <div class="rl-chat-product-title">${esc(title)}</div>
          ${price ? `<div class="rl-chat-product-price">${esc(formatPrice(price))}</div>` : ''}
          <div class="rl-chat-product-cta">${esc(t('voirProduit'))} →</div>
        </div>`;
      container.appendChild(a);
    });
    wrap.appendChild(container);
    messagesEl.appendChild(wrap);
    requestAnimationFrame(() => { messagesEl.scrollTop = messagesEl.scrollHeight; });
  }

  function addTypingIndicator() {
    const w = document.createElement('div');
    w.className = 'rl-chat-msg-wrap assistant rl-chat-typing-wrap';
    w.innerHTML = `<div class="rl-chat-msg assistant"><span class="rl-chat-typing-dots"></span></div>`;
    messagesEl.appendChild(w);
    messagesEl.scrollTop = messagesEl.scrollHeight;
    return w;
  }
  function removeTypingIndicator(el) { if (el?.parentNode) el.parentNode.removeChild(el); }

  async function sendMessage() {
    if (isSending) return;
    const text = inputEl.value.trim();
    if (!text) return;
    isSending = true; sendBtn.disabled = true;
    addMessage('user', text);
    messagesArr.push({ role: 'user', content: text });
    inputEl.value = '';
    if (typingChannel && agentWatching) typingChannel.send({ type: 'broadcast', event: 'cust_typing', payload: { text: '' } });
    const typing = addTypingIndicator();
    
    // Récupère les handles déjà montrés dans cette conversation
    const shownHandles = conversationId 
      ? JSON.parse(localStorage.getItem(`rl_chat_shown_${conversationId}`) || '[]')
      : [];
    
    try {
      const resp = await fetch(`${CONFIG.supabaseUrl}/functions/v1/ai-chat-boutique`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${CONFIG.supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationId,
          currentPageUrl,
          language: getLang(),
          messages: messagesArr,
          previouslyShownHandles: shownHandles,
          sessionId,
          visitorId: rlVisitorId(),
        })
      });
      removeTypingIndicator(typing);
      if (!resp.ok) {
        const txt = await resp.text();
        console.error('[RL Chat] API error:', resp.status, txt);
        addMessage('system', t('errorMsg'));
        return;
      }
      const data = await resp.json();
      if (data.conversationId && data.conversationId !== conversationId) {
        const isNew = !conversationId;
        conversationId = data.conversationId;
        localStorage.setItem('rl_chat_conversation_id', conversationId);
        if (isNew) subscribeToRealtime();
      }
      if (data.botPaused) return;
      if (data.response) {
        addMessage('assistant', data.response);
        messagesArr.push({ role: 'assistant', content: data.response });
      }
      if (data.functionCallResults?.length > 0) {
        const products = extractProducts(data.functionCallResults);
        if (products.length > 0) {
          addProductCards(products);
          
          // Track les nouveaux produits pour dédup dans les follow-ups
          if (conversationId) {
            const storageKey = `rl_chat_shown_${conversationId}`;
            const existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
            const newHandles = products.map(p => p.handle).filter(Boolean);
            const updated = [...new Set([...existing, ...newHandles])];
            localStorage.setItem(storageKey, JSON.stringify(updated));
          }
        }
      }
      // CTA Handoff/Callback (envoyé par ai-chat-boutique quand fallback)
      if (data.cta) {
        renderCTA(data.cta);
      }
    } catch (e) {
      removeTypingIndicator(typing);
      console.error('[RL Chat] Send failed:', e);
      addMessage('system', t('errorMsg'));
    } finally {
      isSending = false; sendBtn.disabled = false;
      inputEl.focus();
    }
  }

  function extractProducts(fcrs) {
    if (!Array.isArray(fcrs)) return [];
    const seen = new Set(); const out = [];
    for (const fcr of fcrs) {
      const arr = fcr.result?.products || fcr.result?.shop_products ||
                  fcr.result?.results || fcr.result?.items || [];
      if (!Array.isArray(arr)) continue;
      for (const p of arr) {
        if (!p?.handle || seen.has(p.handle)) continue;
        seen.add(p.handle); out.push(p);
        if (out.length >= 6) return out;
      }
    }
    return out;
  }

  // ===================================================================
  // CONVERSATION MGMT
  // ===================================================================
  async function loadConversationMessages(convId) {
    if (!supabaseClient || !convId) return;
    try {
      const { data: msgs, error } = await supabaseClient
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', convId)
        .order('created_at', { ascending: true });
      if (error) { console.error('[RL Chat] Load history error:', error); return; }
      messagesEl.innerHTML = '';
      messagesArr = [];
      if (!msgs?.length) return;
      for (const m of msgs) {
        const opts = {};
        if (m.sent_by_agent_id) {
          opts.fromAgent = true;
          opts.agentName = m.display_metadata?.agent_display_name;
        }
        addMessage(m.role, m.content, opts);
        
        // Re-render product cards depuis display_metadata (nouveau v2.1)
        if (m.role === 'assistant' && Array.isArray(m.display_metadata?.products) && m.display_metadata.products.length > 0) {
          addProductCards(m.display_metadata.products);
        }
        
        if (m.role === 'user' || m.role === 'assistant') {
          messagesArr.push({ role: m.role, content: m.content });
        }
      }
      subscribeToRealtime();
    } catch (e) { console.error('[RL Chat] Load history exception:', e); }
  }

  function startNewConversation() {
    // Cleanup shown handles de l'ancienne conv
    if (conversationId) {
      localStorage.removeItem(`rl_chat_shown_${conversationId}`);
    }
    conversationId = null;
    localStorage.removeItem('rl_chat_conversation_id');
    messagesArr = [];
    messagesEl.innerHTML = '';
    unsubscribeRealtime();
    closeHistory();
    inputEl.focus();
  }

  function handleCloseCurrentConv() {
    if (!conversationId) return;
    if (confirm(t('confirmClose'))) startNewConversation();
  }

  // ===================================================================
  // HISTORY VIEW
  // ===================================================================
  async function openHistory() {
    isHistoryOpen = true;
    historyEl.classList.add('open');
    historyListEl.innerHTML = `<div class="rl-chat-history-loading">${esc(t('historyLoading'))}</div>`;
    if (!supabaseClient) {
      await new Promise(r => setTimeout(r, 600));
      if (!supabaseClient) {
        historyListEl.innerHTML = `<div class="rl-chat-history-error">${esc(t('historyError'))}</div>`;
        return;
      }
    }
    try {
      const { data: convs, error } = await supabaseClient
        .from('chat_conversations')
        .select('id, created_at, updated_at, escalated_at')
        .eq('session_id', sessionId)
        .eq('source_site', 'boutique')
        .order('updated_at', { ascending: false })
        .limit(20);
      if (error) {
        console.error('[RL Chat] History fetch error:', error);
        historyListEl.innerHTML = `<div class="rl-chat-history-error">${esc(t('historyError'))}</div>`;
        return;
      }
      if (!convs?.length) {
        historyListEl.innerHTML = `<div class="rl-chat-history-empty">${esc(t('historyEmpty'))}</div>`;
        return;
      }
      const previews = await Promise.all(convs.map(async (c) => {
        const { data: msg } = await supabaseClient
          .from('chat_messages')
          .select('content')
          .eq('conversation_id', c.id)
          .eq('role', 'user')
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        return { conv: c, preview: msg?.content || t('noMessage') };
      }));
      historyListEl.innerHTML = previews.map(({ conv, preview }) => {
        const active = conv.id === conversationId;
        const esc8 = !!conv.escalated_at;
        return `
          <div class="rl-chat-history-card ${active ? 'active' : ''}" data-conv-id="${esc(conv.id)}">
            <div class="rl-chat-history-preview">${esc(preview.slice(0, 120))}</div>
            <div class="rl-chat-history-meta">
              <span>${esc(formatDate(conv.updated_at))}</span>
              ${active ? `<span class="rl-chat-history-badge active">${esc(t('activeBadge'))}</span>` : ''}
              ${esc8 ? `<span class="rl-chat-history-badge escalated">${esc(t('escalatedBadge'))}</span>` : ''}
            </div>
          </div>`;
      }).join('');
      historyListEl.querySelectorAll('.rl-chat-history-card').forEach(card => {
        card.onclick = () => {
          const id = card.dataset.convId;
          if (id !== conversationId) loadPastConversation(id);
          else closeHistory();
        };
      });
    } catch (e) {
      console.error('[RL Chat] History exception:', e);
      historyListEl.innerHTML = `<div class="rl-chat-history-error">${esc(t('historyError'))}</div>`;
    }
  }
  function closeHistory() { isHistoryOpen = false; historyEl.classList.remove('open'); }
  async function loadPastConversation(convId) {
    conversationId = convId;
    localStorage.setItem('rl_chat_conversation_id', convId);
    await loadConversationMessages(convId);
    closeHistory();
  }

  // ===================================================================
  // REALTIME
  // ===================================================================
  function subscribeToRealtime() {
    if (!supabaseClient || !conversationId) return;
    unsubscribeRealtime();
    realtimeChannel = supabaseClient
      .channel(`rl_chat_${conversationId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'chat_messages',
        filter: `conversation_id=eq.${conversationId}`,
      }, (payload) => {
        const m = payload.new;
        if (m.sent_by_agent_id) {
          addMessage(m.role, m.content, {
            fromAgent: true,
            agentName: m.display_metadata?.agent_display_name,
          });
        } else if (m.role === 'system') {
          addMessage('system', m.content);
        }
      })
      .subscribe();
      setupTypingChannel();
  }
  function unsubscribeRealtime() {
    if (supabaseClient && realtimeChannel) {
      supabaseClient.removeChannel(realtimeChannel);
      realtimeChannel = null;
    }
  }

  // ===================================================================
  // HELPERS
  // ===================================================================
  function esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({
      '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
    })[c]);
  }
  function renderMarkdown(s) {
    if (!s) return '';
    let h = esc(s);
    h = h.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    h = h.replace(/(?<![*])\*([^*]+)\*(?![*])/g, '<em>$1</em>');
    h = h.replace(/`([^`]+)`/g, '<code>$1</code>');
    h = h.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, txt, url) => {
      // Filet de sécurité : réécrit le domaine apex vers la boutique
      let href = String(url).trim()
        .replace(/^https?:\/\/(?:www\.)?roulotteslupien\.ca/i, 'https://boutique.roulotteslupien.ca');
      // Liens internes -> même onglet (_top) ; liens externes -> nouvel onglet
      let internal = false;
      try {
        internal = new URL(href, window.location.origin).hostname === window.location.hostname;
      } catch (e) { internal = href.charAt(0) === '/'; }
      const target = internal ? '_top' : '_blank';
      const rel = internal ? '' : ' rel="noopener"';
      return `<a href="${href}" target="${target}"${rel}>${txt}</a>`;
    });
    h = h.replace(/\n/g, '<br/>');
    return h;
  }
  function formatPrice(p) {
    const n = parseFloat(p);
    if (isNaN(n)) return String(p);
    return new Intl.NumberFormat(getLang() === 'en' ? 'en-CA' : 'fr-CA',
      { style: 'currency', currency: 'CAD' }).format(n);
  }
  function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diffDays = Math.floor((Date.now() - d) / 86400000);
    const lang = getLang();
    if (diffDays === 0) return d.toLocaleTimeString(lang === 'en' ? 'en-CA' : 'fr-CA',
      { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return lang === 'en' ? 'Yesterday' : 'Hier';
    if (diffDays < 7) return lang === 'en' ? `${diffDays} days ago` : `Il y a ${diffDays} jours`;
    return d.toLocaleDateString(lang === 'en' ? 'en-CA' : 'fr-CA',
      { day: 'numeric', month: 'short', year: diffDays > 365 ? 'numeric' : undefined });
  }
  // ===================================================================
  // CTA HANDOFF / CALLBACK (boutons + formulaire de rappel)
  // ===================================================================
  function renderCTA(cta) {
    if (!cta || !cta.type || !messagesEl) return;

    const card = document.createElement('div');
    card.className = 'rl-cta-card';
    card.dataset.ctaType = cta.type;

    const labels = cta.labels || {};

        if (cta.type === 'agent_button') {
      const lang = getLang();
      card.innerHTML = `
        <div class="rl-cta-info">${esc(labels.info_in_hours || '')}</div>
        <form class="rl-cta-form" novalidate>
          <div class="rl-cta-field">
            <label>${esc(labels.field_email || (lang === 'en' ? 'Email' : 'Courriel'))} <span class="rl-required">*</span></label>
            <input type="email" name="email" required autocomplete="email" maxlength="150">
          </div>
          <div class="rl-cta-field">
            <label>${esc(labels.field_phone || (lang === 'en' ? 'Phone' : 'Téléphone'))} <span class="rl-cta-help">(${esc(lang === 'en' ? 'optional' : 'optionnel')})</span></label>
            <input type="tel" name="phone" autocomplete="tel" maxlength="30">
          </div>
          <button type="submit" class="rl-cta-button">${esc(labels.button || (lang === 'en' ? 'Talk to a representative' : 'Parler à un représentant'))}</button>
        </form>
      `;
      const form = card.querySelector('.rl-cta-form');
      form.onsubmit = (e) => {
        e.preventDefault();
        handleAgentTransfer(card, cta, form);
      };
    } else if (cta.type === 'callback_form') {
      const summary = cta.conversation_summary || '';
      const timeOpts = labels.time_options || {};
      card.innerHTML = `
        <div class="rl-cta-info">${esc(labels.info_out_hours || '')}</div>
        <form class="rl-cta-form" novalidate>
          <h4 class="rl-cta-form-title">${esc(labels.form_title || 'Demande de rappel')}</h4>
          <div class="rl-cta-field">
            <label>${esc(labels.field_name || 'Nom complet')} <span class="rl-required">*</span></label>
            <input type="text" name="name" required autocomplete="name" maxlength="100">
          </div>
          <div class="rl-cta-field">
            <label>${esc(labels.field_phone || 'Téléphone')} <span class="rl-required">*</span></label>
            <input type="tel" name="phone" required autocomplete="tel" maxlength="30">
          </div>
          <div class="rl-cta-field">
            <label>${esc(labels.field_email || 'Email')} <span class="rl-required">*</span></label>
            <input type="email" name="email" required autocomplete="email" maxlength="150">
          </div>
          <div class="rl-cta-field">
            <label>${esc(labels.field_time || 'Meilleur moment')}</label>
            <select name="preferredTime">
              <option value="">—</option>
              <option value="morning">${esc(timeOpts.morning || 'Avant-midi')}</option>
              <option value="afternoon">${esc(timeOpts.afternoon || 'Après-midi')}</option>
              <option value="evening">${esc(timeOpts.evening || 'En soirée')}</option>
              <option value="anytime">${esc(timeOpts.anytime || "N'importe quand")}</option>
            </select>
          </div>
          <div class="rl-cta-field">
            <label>${esc(labels.field_details || 'Détails de votre demande')}</label>
            <textarea name="reason" rows="4" maxlength="2000">${esc(summary)}</textarea>
            <p class="rl-cta-help">${esc(labels.field_details_help || '')}</p>
          </div>
          <button type="submit" class="rl-cta-button">${esc(labels.submit || 'Envoyer')}</button>
        </form>
      `;
      const form = card.querySelector('.rl-cta-form');
      form.onsubmit = (e) => {
        e.preventDefault();
        handleCallbackSubmit(card, cta, form);
      };
    }

    messagesEl.appendChild(card);
    requestAnimationFrame(() => { messagesEl.scrollTop = messagesEl.scrollHeight; });
  }

    async function handleAgentTransfer(card, cta, form) {
    const btn = card.querySelector('.rl-cta-button');
    const originalText = btn.textContent;
    const lang = getLang();
    removeCTAError(card);

    // Coordonnées : courriel obligatoire, téléphone optionnel.
    const email = (form?.querySelector('[name="email"]')?.value || '').trim();
    const phone = (form?.querySelector('[name="phone"]')?.value || '').trim();
    if (!email) {
      showCTAError(card, lang === 'en' ? 'Email is required.' : 'Le courriel est requis.');
      form?.querySelector('[name="email"]')?.focus();
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showCTAError(card, lang === 'en' ? 'Please enter a valid email.' : 'Veuillez entrer un courriel valide.');
      form?.querySelector('[name="email"]')?.focus();
      return;
    }

    if (!conversationId || !sessionId) {
      showCTAError(card, lang === 'en'
        ? 'Conversation not initialized. Please send a message first.'
        : 'Conversation non initialisée. Envoyez d\'abord un message.');
      return;
    }

    btn.disabled = true;
    btn.textContent = '…';

    try {
      const resp = await fetch(`${CONFIG.supabaseUrl}/functions/v1/chat-transfer-to-agent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${CONFIG.supabaseAnonKey}`,
          'apikey': CONFIG.supabaseAnonKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationId,
          sessionId,
          summary: cta.conversation_summary || '',
          email,
          phone: phone || undefined,
        }),
      });

      if (!resp.ok) {
        const txt = await resp.text();
        console.error('[RL Chat] Transfer error:', resp.status, txt);
        throw new Error(`HTTP ${resp.status}`);
      }

      const data = await resp.json();
      const confirmMsg = cta.labels?.confirmation_button || data.message ||
        (lang === 'en'
          ? 'Your request has been forwarded to our team.'
          : 'Votre demande a été transmise à notre équipe.');
      replaceCTAWithConfirmation(card, confirmMsg);
    } catch (e) {
      console.error('[RL Chat] Transfer failed:', e);
      btn.disabled = false;
      btn.textContent = originalText;
      showCTAError(card, lang === 'en'
        ? 'An error occurred. Please try again or call us at 1-888-897-4949.'
        : 'Une erreur est survenue. Veuillez réessayer ou nous appeler au 1-888-897-4949.');
    }
  }

  async function handleCallbackSubmit(card, cta, form) {
    removeCTAError(card);
    const fd = new FormData(form);
    const name = (fd.get('name') || '').toString().trim();
    const phone = (fd.get('phone') || '').toString().trim();
    const email = (fd.get('email') || '').toString().trim();
    const preferredTime = (fd.get('preferredTime') || '').toString();
    const reason = (fd.get('reason') || '').toString().trim();

    if (!name) {
      showCTAError(card, getLang() === 'en' ? 'Name is required.' : 'Le nom est requis.');
      form.querySelector('[name="name"]').focus();
      return;
    }
    if (!phone) {
      showCTAError(card, getLang() === 'en' ? 'Phone is required.' : 'Le téléphone est requis.');
      form.querySelector('[name="phone"]').focus();
      return;
    }
    if (!email) {
      showCTAError(card, getLang() === 'en' ? 'Email is required.' : "L'email est requis.");
      form.querySelector('[name="email"]').focus();
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showCTAError(card, getLang() === 'en' ? 'Please enter a valid email.' : 'Veuillez entrer un courriel valide.');
      form.querySelector('[name="email"]').focus();
      return;
    }

    const submitBtn = form.querySelector('button[type="submit"]');
    const originalText = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = '…';

    try {
      const resp = await fetch(`${CONFIG.supabaseUrl}/functions/v1/chat-callback-request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${CONFIG.supabaseAnonKey}`,
          'apikey': CONFIG.supabaseAnonKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationId: conversationId || null,
          sessionId: sessionId || null,
          name, phone, email, preferredTime, reason,
        }),
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        console.error('[RL Chat] Callback error:', resp.status, errData);
        throw new Error(errData.error || `HTTP ${resp.status}`);
      }

      const firstName = name.split(/\s+/)[0];
      const confirmTemplate = cta.labels?.confirmation_form ||
        (getLang() === 'en'
          ? 'Thanks {firstName}! We\'ll contact you on the next business day.'
          : 'Merci {firstName} ! Nous vous contacterons au prochain jour ouvrable.');
      const confirmMsg = confirmTemplate.replace('{firstName}', firstName);
      replaceCTAWithConfirmation(card, confirmMsg);
    } catch (e) {
      console.error('[RL Chat] Callback submit failed:', e);
      submitBtn.disabled = false;
      submitBtn.textContent = originalText;
      showCTAError(card, getLang() === 'en'
        ? 'An error occurred. Please try again.'
        : 'Une erreur est survenue. Veuillez réessayer.');
    }
  }

  function replaceCTAWithConfirmation(card, message) {
    const confirmEl = document.createElement('div');
    confirmEl.className = 'rl-cta-confirmation';
    confirmEl.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M3 8l3 3 7-7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>${esc(message)}</span>
    `;
    card.replaceWith(confirmEl);
    if (messagesEl) {
      requestAnimationFrame(() => { messagesEl.scrollTop = messagesEl.scrollHeight; });
    }
  }

  function showCTAError(card, message) {
    let errEl = card.querySelector('.rl-cta-error');
    if (!errEl) {
      errEl = document.createElement('div');
      errEl.className = 'rl-cta-error';
      const submitBtn = card.querySelector('.rl-cta-button');
      if (submitBtn && submitBtn.parentNode) {
        submitBtn.parentNode.insertBefore(errEl, submitBtn);
      } else {
        card.appendChild(errEl);
      }
    }
    errEl.textContent = message;
    errEl.style.display = 'block';
  }

  function removeCTAError(card) {
    const errEl = card.querySelector('.rl-cta-error');
    if (errEl) errEl.style.display = 'none';
  }

  // ===================================================================
  // TEASER (bulle d'accroche pour inciter au clic)
  // ===================================================================
  function initTeaser() {
    // Réglages
    const DELAY_MS = parseInt(scriptTag?.dataset.teaserDelay || '4000', 10); // délai avant apparition
    const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;   // silence après un « × » explicite
    const COOLDOWN_MS = 24 * 60 * 60 * 1000;       // ne pas réafficher avant 24 h
    const now = Date.now();

    // Conditions de non-affichage
    try {
      const dismissed = parseInt(localStorage.getItem('rl_chat_teaser_dismissed') || '0', 10);
      if (dismissed && now - dismissed < DISMISS_MS) return;
      const shown = parseInt(localStorage.getItem('rl_chat_teaser_shown') || '0', 10);
      if (shown && now - shown < COOLDOWN_MS) return;
    } catch (e) { /* localStorage indisponible : on continue */ }
    if (conversationId) return; // déjà engagé dans une conversation

    // Message bilingue — modifie le texte ici au besoin
    const msg = getLang() === 'en'
      ? '\uD83D\uDC4B A question? I can help you find the right part.'
      : '\uD83D\uDC4B Une question? Je peux vous aider \u00E0 trouver la bonne pi\u00E8ce.';

    // Styles
    const css = `
.rl-chat-teaser {
  position: fixed; right: 24px; bottom: 96px;
  max-width: 250px; z-index: 9997;
  background: #fff; color: #1a1a1a;
  border-radius: 14px; border: 1px solid #e5e7eb;
  box-shadow: 0 8px 24px rgba(0,0,0,.16);
  padding: 12px 34px 12px 14px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
  font-size: 14px; line-height: 1.4;
  cursor: pointer; opacity: 0; transform: translateY(8px);
  transition: opacity .25s ease, transform .25s ease;
}
.rl-chat-teaser.show { opacity: 1; transform: translateY(0); }
.rl-chat-teaser::after {
  content: ''; position: absolute; right: 26px; bottom: -7px;
  width: 14px; height: 14px; background: #fff;
  border-right: 1px solid #e5e7eb; border-bottom: 1px solid #e5e7eb;
  transform: rotate(45deg);
}
.rl-chat-teaser-close {
  position: absolute; top: 5px; right: 7px;
  width: 20px; height: 20px; border: none; background: transparent;
  color: #9ca3af; cursor: pointer; font-size: 17px; line-height: 1;
  display: flex; align-items: center; justify-content: center; border-radius: 50%;
}
.rl-chat-teaser-close:hover { background: #f3f4f6; color: #1a1a1a; }
@media (max-width: 480px) { .rl-chat-teaser { max-width: 210px; bottom: 92px; } }
.rl-chat-fab.rl-pulse { animation: rl-fab-pulse 1.8s ease-in-out 3; }
@keyframes rl-fab-pulse {
  0%   { box-shadow: 0 4px 12px rgba(0,0,0,.2), 0 0 0 0 ${CONFIG.colorPrimary}66; }
  70%  { box-shadow: 0 4px 12px rgba(0,0,0,.2), 0 0 0 14px ${CONFIG.colorPrimary}00; }
  100% { box-shadow: 0 4px 12px rgba(0,0,0,.2), 0 0 0 0 ${CONFIG.colorPrimary}00; }
}
@media (prefers-reduced-motion: reduce) {
  .rl-chat-fab.rl-pulse { animation: none; }
  .rl-chat-teaser { transition: none; }
}`;
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);

    // Élément
    const teaser = document.createElement('div');
    teaser.className = 'rl-chat-teaser';
    teaser.setAttribute('role', 'button');
    teaser.setAttribute('tabindex', '0');
    teaser.innerHTML = `<button class="rl-chat-teaser-close" aria-label="${esc(t('close'))}">&times;</button><span>${esc(msg)}</span>`;

    const removeTeaser = () => {
      teaser.classList.remove('show');
      if (fab) fab.classList.remove('rl-pulse');
      setTimeout(() => teaser.remove(), 250);
    };
    const openChat = () => {
      removeTeaser();
      if (!isPanelOpen) togglePanel();
    };
    const dismiss = (e) => {
      if (e) e.stopPropagation();
      try { localStorage.setItem('rl_chat_teaser_dismissed', String(Date.now())); } catch (err) {}
      removeTeaser();
    };

    teaser.addEventListener('click', openChat);
    teaser.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openChat(); }
    });
    teaser.querySelector('.rl-chat-teaser-close').addEventListener('click', dismiss);

    // Apparition différée
    setTimeout(() => {
      if (isPanelOpen) return; // l'utilisateur a déjà ouvert le chat entre-temps
      try { localStorage.setItem('rl_chat_teaser_shown', String(Date.now())); } catch (err) {}
      document.body.appendChild(teaser);
      requestAnimationFrame(() => teaser.classList.add('show'));
      if (fab) fab.classList.add('rl-pulse');
    }, DELAY_MS);
  }

  // ===================================================================
  // HEARTBEAT DE PRÉSENCE (indique à l'admin que le client est encore là)
  // ===================================================================
  function sendHeartbeat() {
    if (!conversationId) return;                       // rien à signaler sans conversation
    if (document.visibilityState !== 'visible') return; // onglet en arrière-plan => pas "présent"
    var url = CONFIG.supabaseUrl + '/functions/v1/chat-presence';
    var body = JSON.stringify({ conversationId: conversationId, visitorId: rlVisitorId() });
    try {
      // sendBeacon (text/plain) : sans en-tête, survit à la navigation — comme le pixel commerce
      if (navigator.sendBeacon) {
        var blob = new Blob([body], { type: 'text/plain' });
        if (navigator.sendBeacon(url, blob)) return;
      }
    } catch (e) { /* fallback ci-dessous */ }
    try {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: body, keepalive: true })
        .catch(function () {});
    } catch (e) {}
  }

  function startHeartbeat() {
    sendHeartbeat();                                   // battement immédiat
    setInterval(sendHeartbeat, 25000);                 // puis toutes les 25 s
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') sendHeartbeat();
    });
  }

  // ===================================================================
  // APERÇU DE FRAPPE EN DIRECT (l'agent voit ce que le client tape)
  // ===================================================================
  let typingChannel = null;
  let agentWatching = false;
  let typingThrottle = 0;

  function setupTypingChannel() {
    if (!supabaseClient || !conversationId) return;
    if (typingChannel) { supabaseClient.removeChannel(typingChannel); typingChannel = null; }
    agentWatching = false;
    typingChannel = supabaseClient.channel('rl_typing_' + conversationId, {
      config: { presence: { key: 'visitor' } }
    });
    typingChannel.on('presence', { event: 'sync' }, function () {
      var state = typingChannel.presenceState();
      agentWatching = Object.keys(state).some(function (k) {
        return state[k].some(function (p) { return p.role === 'agent'; });
      });
      if (!agentWatching) return;
      // Un agent vient de regarder : pousse l'état courant tout de suite
      typingChannel.send({ type: 'broadcast', event: 'cust_typing', payload: { text: (inputEl && inputEl.value) || '' } });
    });
    typingChannel.subscribe();
  }

  function broadcastTyping() {
    if (!agentWatching || !typingChannel) return;
    var now = Date.now();
    if (now - typingThrottle < 250) return;   // limite le débit
    typingThrottle = now;
    typingChannel.send({ type: 'broadcast', event: 'cust_typing', payload: { text: (inputEl && inputEl.value) || '' } });
  }
  
  // ===================================================================
  // BOOT
  // ===================================================================
  function boot() {
    initSession();
    injectStyles();
    buildDOM();
    loadSupabaseSDK();
    initTeaser();
    startHeartbeat();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
})();
